import { DateTime } from "luxon";
import { useMemo } from "react";
import type { CalendarPagerBounds } from "../../components/CustomCalendar/useCalendarPager";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserShifts } from "../../hooks/useUserShifts";
import { getPtoStaffingDays, getShiftDisplayDate, isFullStaffingDayPto } from "../../utils/dateUtils";
import { type Nurse, type NurseInShift, Preferences, ShiftType, type SpecificPreference, type TransformedShift, type WeekData } from "../../utils/models";
import { compareByDateThenStart } from "./components/groupShiftsByWeek";

// HRIS feeds send whole days as midnight to midnight, off the day-start boundary, so the one-day check is what
// keeps those reading "Day Off" instead of "12:00 AM - 12:00 AM". Calendar days, not 24h, so DST days count too.
const getPartialTimeOffWindow = (pref: SpecificPreference, staffingDays: DateTime[], dayStart: string): TransformedShift["timeOffWindow"] => {
	const isPartialDay = pref.end_date < pref.start_date.plus({ days: 1 }) && !isFullStaffingDayPto(pref.start_date, pref.end_date, dayStart);
	if (staffingDays.length !== 1 || !isPartialDay) return undefined;
	return { start: pref.start_date, end: pref.end_date, hours: pref.hours };
};

// Keyed by the staffing day at midnight, the same key fillEmptyDays uses, so a day with time off gets no empty row too.
const addPtoToShiftsMap = (shiftsMap: Map<string, TransformedShift[]>, preferences: SpecificPreference[] | undefined, dayStart: string): void => {
	preferences
		?.filter((pref) => pref.type === Preferences.Avoid)
		.forEach((pref) => {
			const staffingDays = getPtoStaffingDays(pref.start_date, pref.end_date, dayStart);
			const timeOffWindow = getPartialTimeOffWindow(pref, staffingDays, dayStart);

			for (const day of staffingDays) {
				const dateKey = day.toISO() as string;
				const sameDayTimeOff = shiftsMap.get(dateKey);
				const timeOffRow: TransformedShift = {
					date: day,
					hasDayRequest: false,
					hasNightRequest: false,
					isTimeOff: true,
					timeOffType: "Day Off",
					timeOffReason: pref.reason || "",
					timeOffWindow,
					isPreferredNot: false,
					isPreferred: false,
					requests: 0,
					departmentId: "",
				};
				shiftsMap.set(dateKey, sameDayTimeOff ? [...sameDayTimeOff, timeOffRow] : [timeOffRow]);
			}
		});
};

// `graceMinutes` is for SHIFTS only. The PTO function above stays on the raw dayStart on purpose:
// whole-day PTO runs from exactly dayStart to the next one, so moving the boundary adds a day to it.
const addNurseShiftToMap = (shiftsMap: Map<string, TransformedShift[]>, nurseShift: NurseInShift, dayStart: string, graceMinutes: number): void => {
	const shiftType = nurseShift.startTime.hour >= 18 || nurseShift.startTime.hour < 6 ? ShiftType.Night : ShiftType.Day;
	const adjustedDate = getShiftDisplayDate(nurseShift.startTime, dayStart, graceMinutes);
	const adjustedDateKey = adjustedDate.toISO() as string;
	// Drop the empty-day placeholder, never time off: a shift starting at midnight shares the time-off key.
	const existing = shiftsMap.get(adjustedDateKey)?.filter((s) => s.id != null || s.isTimeOff) ?? [];

	shiftsMap.set(adjustedDateKey, [
		...existing,
		{
			id: nurseShift.id,
			date: adjustedDate,
			startTime: nurseShift.startTime,
			endTime: nurseShift.endTime,
			location: nurseShift.location || "",
			shiftType,
			isCharge: nurseShift.charge,
			hasDayRequest: false,
			hasNightRequest: false,
			isTimeOff: false,
			timeOffType: undefined,
			timeOffReason: undefined,
			isPreferredNot: false,
			isPreferred: false,
			requests: 0,
			bonus: nurseShift.bonus,
			departmentId: nurseShift.departmentId,
			notes: nurseShift.notes,
			jobName: nurseShift.jobName,
			jobId: nurseShift.jobId,
		},
	]);
};

const fillEmptyDays = (shiftsMap: Map<string, TransformedShift[]>, minDate: DateTime, maxDate: DateTime): void => {
	let currentDate = minDate.startOf("day");
	const endDate = maxDate.endOf("day");

	while (currentDate.startOf("day") <= endDate.startOf("day")) {
		const dateKey = currentDate.toISO() as string;
		if (!shiftsMap.has(dateKey)) {
			shiftsMap.set(dateKey, [
				{
					date: currentDate,
					hasDayRequest: false,
					hasNightRequest: false,
					isTimeOff: false,
					timeOffType: undefined,
					isPreferredNot: false,
					isPreferred: false,
					requests: 0,
					departmentId: "",
				},
			]);
		}
		currentDate = currentDate.plus({ day: 1 });
	}
};

export const useBedsideNurse = () => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { data: shifts } = useUserShifts();

	const userShifts = useMemo<TransformedShift[]>(() => {
		if (!shifts || !hospitalData?.nurses.length) return [];

		const nurseIds: number[] = userInfo.nurses_info.map((n) => n.nurse.id);
		const nurses = hospitalData.nurses.filter((n: Nurse) => nurseIds.includes(n.id));

		if (!nurses || nurses.length === 0) return [];

		const shiftsMap = new Map<string, TransformedShift[]>();

		nurses.forEach((nurse) => {
			addPtoToShiftsMap(shiftsMap, nurse.employeeSpecificPreferences, chosenDepartment.dayStart);
		});

		Object.values(shifts)
			.flat()
			.forEach((nurseShift) => {
				addNurseShiftToMap(shiftsMap, nurseShift, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
			});

		const dateKeys = Object.keys(shifts).sort();
		const minDateKey = dateKeys.at(0);
		const maxDateKey = dateKeys.at(-1);

		if (!minDateKey || !maxDateKey) return [];

		const minDate = DateTime.fromFormat(minDateKey, "yyyy-MM-dd");
		const maxDate = DateTime.fromFormat(maxDateKey, "yyyy-MM-dd");

		if (minDate && maxDate) {
			fillEmptyDays(shiftsMap, minDate, maxDate);
		}

		return Array.from(shiftsMap.values()).flat().sort(compareByDateThenStart);
	}, [userInfo.nurses_info, hospitalData?.nurses, shifts, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	// The days the server sent a schedule for, so the calendar never pages past what the list can show.
	const scheduleBounds = useMemo<CalendarPagerBounds | undefined>(() => {
		if (!shifts) return undefined;
		const dateKeys = Object.keys(shifts).sort();
		const firstDateKey = dateKeys.at(0);
		const lastDateKey = dateKeys.at(-1);
		if (!firstDateKey || !lastDateKey) return undefined;
		return { firstDay: DateTime.fromISO(firstDateKey), lastDay: DateTime.fromISO(lastDateKey) };
	}, [shifts]);

	return { userShifts, scheduleBounds };
};

/** One nurse's shifts and PTO for the profile's calendar preview, one entry per day across `weeks`. */
export const buildNurseShifts = (weeks: WeekData[], nurse: Nurse, dayStart: string, scheduledShiftDeltaMinutes: number): TransformedShift[] => {
	const shiftsMap = new Map<string, TransformedShift[]>();

	addPtoToShiftsMap(shiftsMap, nurse.employeeSpecificPreferences, dayStart);

	let minDate: DateTime | undefined;
	let maxDate: DateTime | undefined;

	weeks.forEach((week) => {
		week.weeklyShifts.forEach((shift) => {
			shift.days.forEach((day) => {
				if (!minDate || day.date < minDate) minDate = day.date;
				if (!maxDate || day.date > maxDate) maxDate = day.date;

				day.nurses
					.filter((n) => n.nurseId === nurse.id)
					.forEach((nurseShift) => {
						addNurseShiftToMap(shiftsMap, nurseShift, dayStart, scheduledShiftDeltaMinutes);
					});
			});
		});
	});

	if (minDate && maxDate) {
		fillEmptyDays(shiftsMap, minDate, maxDate);
	}

	return Array.from(shiftsMap.values()).flat().sort(compareByDateThenStart);
};
